import React, { FC, useEffect } from 'react';
import {
  Container,
  FileInfoContainer,
  InfoContainer,
  Name,
  SizeCategoryContainer,
  ActionContainer,
  CategoryTextContainer,
  DateInfo,
  ErrorMessage,
  StatusMessage,
} from './DocumentCard.styles';
import FileFormatIcon from 'components/atoms/file-format-icon';

import { DocumentCardTypes } from './types';
import { ellipsisText } from 'helpers/strings-functions';
import { useTheme } from 'styled-components';
import { Icon } from 'components/atoms';
import Loading from 'components/atoms/loading';
import dayjs from 'dayjs';
import { INITIALDOWNLOADSTATE } from 'hooks/useDownloadState';

const DocumentCard: FC<DocumentCardTypes> = ({
  fileType,
  name,
  date,
  expiryDate,
  category,
  showCategory = true,
  actionComponent,
  fullWidth,
  option,
  tapSelect,
  onOptionClick,
  onSelect,
  isSelected,
  fileSize,
  extendedNames = false,
  downloadState = { progress: 0, loading: false, failed: false },
  autoResetFailed,
  errorMessage,
  onAutoReset,
  fileStatus,
}: DocumentCardTypes) => {
  const theme = useTheme();
  const normalizedFileStatus = fileStatus?.toLowerCase();
  const isFileStatusInProgress = normalizedFileStatus === 'in progress';
  const isFileStatusError = normalizedFileStatus === 'error';

  const isInProgress =
    isFileStatusInProgress ||
    downloadState.loading ||
    (downloadState.progress > 0 && downloadState.progress < 100);
  const isError = isFileStatusError || !!downloadState.failed;
  const displayErrorMessage = errorMessage ?? 'Upload failed.';
  const displayStatusMessage = isFileStatusInProgress ? 'Scanning in progress' : 'Uploading';

  useEffect(() => {
    if (!downloadState.failed || autoResetFailed === false) return;
    const t = setTimeout(() => onAutoReset?.(), 5000);
    return () => clearTimeout(t);
  }, [autoResetFailed, downloadState.failed, onAutoReset]);

  const handleSelect = () => {
    if (option === 'withSelect') {
      onSelect(!isSelected);
    }
    handleOnSelectOptionClick();
  };

  const handleOnSelectOptionClick = () => {
    if (onOptionClick) {
      onOptionClick();
    }
  };

  const convertFileSize = (bytes) => {
    if (bytes === undefined || bytes === null) return null;

    const kb = bytes / 1024;
    const mb = kb / 1024;
    const gb = mb / 1024;

    if (gb >= 1) {
      return `${gb.toFixed(1)} GB`;
    } else if (mb >= 1) {
      return `${mb.toFixed(1)} MB`;
    } else if (kb >= 1) {
      return `${kb.toFixed(1)} KB`;
    } else {
      return `${bytes} B`;
    }
  };

  const formatFileSize = convertFileSize(fileSize);

  const formatDate = (value?: string | null) => (value ? dayjs(value).format('DD/MM/YYYY') : '-');

  const formattedDate = formatDate(date);

  const formattedExpiryDate = expiryDate && formatDate(expiryDate);

  const getBorderColor = () => {
    if (isError) {
      return '#ef3826';
    }

    if (isSelected) {
      return theme.colours.crowberryBlue;
    }

    return theme.colours.spanishGrey;
  };

  return (
    <Container
      data-testid="DocumentCardContainer"
      className="documentCard"
      selected={isSelected}
      fullWidth={fullWidth}
      title={name}
      onClick={tapSelect ? () => handleSelect() : () => {}}
      isError={isError}
      style={{
        border: `1px solid ${getBorderColor()}`,
      }}
    >
      <FileInfoContainer>
        <FileFormatIcon fileType={fileType} />
        <InfoContainer>
          <Name>{ellipsisText(name, extendedNames ? 25 : 18)}</Name>
          {isError ? (
            <SizeCategoryContainer>
              <CategoryTextContainer>
                {showCategory && category ? ellipsisText(category, extendedNames ? 42 : 32) : ''}
              </CategoryTextContainer>
              <ErrorMessage>{displayErrorMessage}</ErrorMessage>
            </SizeCategoryContainer>
          ) : isInProgress ? (
            <SizeCategoryContainer>
              <CategoryTextContainer>
                {showCategory && category ? ellipsisText(category, extendedNames ? 42 : 32) : ''}
              </CategoryTextContainer>
              <StatusMessage>{displayStatusMessage}</StatusMessage>
            </SizeCategoryContainer>
          ) : (
            <SizeCategoryContainer>
              {!fileSize ? (
                showCategory && category ? (
                  <CategoryTextContainer>
                    {ellipsisText(category, extendedNames ? 42 : 32)}
                  </CategoryTextContainer>
                ) : null
              ) : (
                <CategoryTextContainer>
                  {formatFileSize} • {'.' + fileType}
                </CategoryTextContainer>
              )}
              <p>
                <DateInfo>
                  Created: {formattedDate}
                  {formattedExpiryDate && <> | Deletion: {formattedExpiryDate}</>}
                </DateInfo>
              </p>
            </SizeCategoryContainer>
          )}
        </InfoContainer>
      </FileInfoContainer>
      <ActionContainer isSelected={isSelected} isInProgress={isInProgress}>
        {isInProgress ? <Loading /> : actionComponent ? actionComponent : null}
        {option === 'withRemove' ? (
          <div className="close-box" onClick={handleOnSelectOptionClick}>
            <Icon icon="close" size="xxxxxs" colour={theme.colours.agedGray} cursor="pointer" />
          </div>
        ) : null}
        {option === 'withSelect' ? (
          <div className="select-box" onClick={handleSelect}>
            <Icon icon="check" size="xxxxs" colour={theme.colours.white} cursor="pointer" />
          </div>
        ) : null}
      </ActionContainer>
    </Container>
  );
};

export default DocumentCard;
